import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, isNil } from "lodash";

import fpStockService from "@plannel/services/fp/fp-stock-service";
import InventoryPopup from "@plannel/pages/factory-plan/common/popup/InventoryPopup";
import CodePickerRenderer from "@plannel/pages/factory-plan/common/component/CodePickerRenderer";
import PlanScope from "@plannel/pages/factory-plan/common/component/PlanScope";
import SearchOptsGroup from "@plannel/pages/factory-plan/common/component/SearchOptsGroup";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState";
import FpGridSetting, { useCommonCodeValues, codeComboCellEditorParams, findMissingRequired, focusInvalidCell } from "@plannel/pages/factory-plan/common/grid";
import { AddButton, DownloadButton, RemoveButton, SaveButton } from "@plannel/components/ActionIconButton";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import NumberUtil from "@plannel/utils/number-util";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";

// old SearchOptsGroup 카탈로그 중 이 화면이 쓰는 3개 — §10: 고정 카탈로그, DB 동적 표시제어 없음.
const SEARCH_FIELD_LABELS = { itemCdNm: "FP_ITEM", inventoryCdNm: "FP_INVENTORY", stockCdNm: "FP_STOCK" };
const SEARCH_FIELD_KEYS = Object.keys(SEARCH_FIELD_LABELS);

// old validRules required 5개(Stock.jsx:42/51/73/83/93) 전부. qty/activeYn은 엔티티가
// @PrePersist로 기본값(0/'Y')을 채워주지만 그건 INSERT 경로에만 걸린다 — 기존 행 수정은
// FpPersistenceService.saveAll의 merge() = UPDATE라 보정이 없어, 값을 지운 채 저장하면
// qty/active_yn NOT NULL 위반으로 배치 전체가 실패한다. 그래서 프론트에서 먼저 막는다.
const REQUIRED_FIELDS = [
  { field: "stockCd", label: "STOCK_CD" },
  { field: "inventoryCd", label: "FP_INVENTORY_CD" },
  { field: "qty", label: "AVAIL_QTY" },
  { field: "receiptTs", label: "RECEIPT_DATE" },
  { field: "activeYn", label: "FP_USE_YN" }
];

const { snackBarMessageColor } = ColorUtil;

const Stock = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [planScope, setPlanScope] = useState(reduxViewState?.planScope ?? "");
  const [searchFields, setSearchFields] = useState({
    itemCdNm: reduxViewState?.itemCdNm ?? "",
    inventoryCdNm: reduxViewState?.inventoryCdNm ?? "",
    stockCdNm: reduxViewState?.stockCdNm ?? "",
  });
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [progressOn, setProgressOn] = useState(false);
  const [inventoryPopup, setInventoryPopup] = useState({ open: false, node: null });

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);
  const [stock, setStock] = useState([]);

  // 무한 재고 사용 타입(FP_INFINITE_TP_CD) 콤보 값 — §8 공통코드 콤보 공통 훅(common/grid.js).
  // old getCodeEditor('FP_INFINITE_TP_CD') 대응.
  const infiniteTpValuesRef = useCommonCodeValues("FP_INFINITE_TP_CD");

  const latestReqRef = useRef(0);

  const defaultGridMemo = useMemo(() => FpGridSetting({ title, viewName }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);

  // planScope는 서버 조회 조건, 나머지 3개(품목/인벤토리/재고 코드·명칭)는 AG Grid 외부 필터
  // (isExternalFilterPresent/doesExternalFilterPass)로 즉시 필터(§6-1 케이스3, Route.js/Resource.js와 동일 패턴).
  // rowData 배열 자체는 절대 바꾸지 않는다 — rowData를 매번 새 배열로 갈아치우면 getRowId 없는
  // 상태에서 grid row model이 통째로 재생성되어, applyTransaction으로 추가한 미저장 신규 행이
  // 조회조건 변경 시 사라지는 버그가 있었다(§12 흔한 함정 참고).
  // 재고는 명칭(stockNm) 컬럼이 없어 stockCdNm은 stockCd만 부분일치로 본다(서버 makeWhereClause와 동일 필드).
  // TODO(MIG-T2): [stock/Stock] 재조회·필터 시 미저장 변경 경고(MSG_5142) 미이관.
  //   old: handleKeyPress에서 getViewIsUpdated(viewId)가 true면 MSG_5142 확인을 받은 뒤에만 재조회.
  //   사유: 2026-08-05 사용자 결정 — Route/Resource와 동일하게 유지(FP master 3화면 공통, 패턴 차원 과제).
  const doesExternalFilterPass = (node) => {
    if (!node.data?.id) return true;   // 미저장 신규 행은 조회조건과 무관하게 항상 표시(편집 중 초안)
    const { itemCdNm, inventoryCdNm, stockCdNm } = searchFields;
    const hit = (hay, needle) => (hay ?? "").toLowerCase().includes(needle.toLowerCase());
    const r = node.data;
    if (itemCdNm && !(hit(r.itemCd, itemCdNm) || hit(r.itemNm, itemCdNm))) return false;
    if (inventoryCdNm && !(hit(r.inventoryCd, inventoryCdNm) || hit(r.inventoryNm, inventoryCdNm))) return false;
    if (stockCdNm && !hit(r.stockCd, stockCdNm)) return false;
    return true;
  };

  const isExternalFilterPresent = () => Object.values(searchFields).some(v => !!v);

  // searchFields는 AG Grid가 직접 구독하는 값이 아니라서, 바뀔 때마다 명시적으로 재평가를 트리거.
  useEffect(() => {
    gridRef.current?.api?.onFilterChanged();
  }, [searchFields]);

  const onSearchFieldChange = (tpCd, value) => setSearchFields(prev => ({ ...prev, [tpCd]: value }));

  // TODO(MIG-T2): [stock/Stock] 컬럼 자동필터(autoFilter) 미이관.
  //   old: stockCd/inventoryCd/itemCd 등 컬럼에 RealGrid autoFilter: true.
  //   사유: 컬럼 필터는 Tier2(1차 유보) 표준 항목 — Route/Resource와 동일하게 미적용.
  const columnDefs = [
    // id는 axios-bigint의 useNativeBigInt로 파싱돼 typeof "bigint" — AG Grid 타입 추론이
    // number/string/boolean/date 어디에도 안 걸려 "object"로 폴백해 경고를 내므로 추론을 끈다.
    { headerName: "ID", field: "id", colId: "id", hide: true, type: ["nonEditableColumn"], cellDataType: false },
    { headerName: "FP_PLAN_SCOPE", field: "planScope", colId: "planScope", hide: true, type: ["nonEditableColumn"] },
    // old setCodeColumnStyle 대응 — 신규 행(id 없음)만 코드 편집 가능, 저장된 행은 PK성 코드 잠금(§3-3).
    { headerName: "STOCK_CD", field: "stockCd", colId: "stockCd", cellClass: "stringType", width: 180,
      editable: params => !params.data?.id },
    { headerName: "FP_DESC_TXT", field: "descTxt", colId: "descTxt", cellClass: "stringType", width: 110,
      cellEditorParams: { maxLength: 500 } },
    // 품목 상세 그룹(2줄 헤더, 접기/펼치기) — old itemDetailGroup(expanded:false) 대응.
    // 접힘: 인벤토리 코드+품목 코드(old groupShowMode "always" 2개), 펼침: 명칭/구분 추가.
    {
      headerName: "FP_ITEM_DETAIL", groupId: "itemDetail", marryChildren: true, openByDefault: false,
      children: [
        { headerName: "FP_INVENTORY_CD", field: "inventoryCd", colId: "inventoryCd", cellClass: "stringType", width: 180,
          editable: false, cellRenderer: CodePickerRenderer,
          cellRendererParams: { onOpenPopup: node => setInventoryPopup({ open: true, node }) } },
        { headerName: "FP_INVENTORY_NAME", field: "inventoryNm", colId: "inventoryNm", type: ["nonEditableColumn"], width: 240, columnGroupShow: "open" },
        { headerName: "FP_ITEM_CD", field: "itemCd", colId: "itemCd", type: ["nonEditableColumn"], width: 180 },
        { headerName: "FP_ITEM_NM", field: "itemNm", colId: "itemNm", type: ["nonEditableColumn"], width: 240, columnGroupShow: "open" },
        { headerName: "FP_ITEM_CLASS_CD", field: "itemClassCd", colId: "itemClassCd", type: ["nonEditableColumn", "centerAligned"], width: 110, columnGroupShow: "open" }
      ]
    },
    { headerName: "USABLE_DATE", field: "usableTs", colId: "usableTs", type: ["dateTimeColumn"], width: 150 },
    { headerName: "EXPIRE_DATE", field: "expireTs", colId: "expireTs", type: ["dateTimeColumn"], width: 150 },
    // TODO(MIG-T2): [stock/Stock] qty=0 셀 텍스트 색상 강조(styleCallback) 미이관.
    //   old: 값이 0이면 'editable-number-column text-color-black' 스타일 추가.
    //   사유: 장식성 렌더러(Tier2) — 값 편집/표시 자체는 정상 이관.
    { headerName: "AVAIL_QTY", field: "qty", colId: "qty", width: 110, type: ["numberColumn", "rightAligned"],
      // old editFormat(로케일, 소수 5자리) 대응 — 가드는 공용 numberColumn 타입과 동일 기준.
      valueFormatter: p => (isNil(p.value) || p.value === "" || !isFinite(p.value)) ? "" : NumberUtil.formatNumberData(p, { maximumFractionDigits: 5, minimumFractionDigits: 0 }),
      cellEditor: "agNumberCellEditor", cellEditorParams: { min: 0 } },
    // 발주/입고 정보 그룹 — old orderReceiptGroup(접기 없음, 6컬럼 상시 표시) 대응.
    // 앞 5컬럼은 조회 전용(주문 테이블에서 온 값). new 백엔드는 아직 이 필드를 채우지 않아
    // 지금은 빈 값으로 보이지만(backend-gaps.md #1 — 조회 조인 미이관), StockDto에 필드가 있어
    // 백엔드가 채우는 순간 그대로 표시된다. old와 동일하게 컬럼은 유지한다.
    {
      headerName: "FP_ORDER_RECEIPT_INFO", groupId: "orderReceipt", marryChildren: true,
      children: [
        { headerName: "FP_ORDER_NUMBER", field: "orderNumber", colId: "orderNumber", type: ["nonEditableColumn"], width: 150 },
        { headerName: "PO_DATE", field: "orderTs", colId: "orderTs", type: ["nonEditableColumn"], width: 150, valueFormatter: dateUtils.formatDateTime },
        { headerName: "SALES", field: "account", colId: "account", type: ["nonEditableColumn"], width: 110 },
        { headerName: "FP_ORDER_MANAGER", field: "orderManager", colId: "orderManager", type: ["nonEditableColumn"], width: 150 },
        { headerName: "FP_EXPECTED_RECEIPT_DATE", field: "expectedReceiptTs", colId: "expectedReceiptTs", type: ["nonEditableColumn"], width: 150, valueFormatter: dateUtils.formatDateTime },
        { headerName: "RECEIPT_DATE", field: "receiptTs", colId: "receiptTs", type: ["dateTimeColumn"], width: 150 }
      ]
    },
    { headerName: "FP_PRIORITY", field: "priority", colId: "priority", width: 110, type: ["numberColumn", "rightAligned"],
      cellEditor: "agNumberCellEditor", cellEditorParams: { min: 0, precision: 0 } },
    // TODO(MIG-T2): [stock/Stock] 주문 페깅(orderPeggingCd) 편집(OrderPopup 다중선택) 미이관 — 컬럼 표시만 이관.
    //   old: 셀 action 버튼 → OrderPopup 다중선택 → ", " join 표시, 저장 시 Dmnd/WoStockAssign 테이블로 역분해 저장.
    //   사유: 백엔드 저장 API 미이관 — backend-gaps.md #1 (StockDto.toEntity()가 이 필드를 저장하지 않고
    //   할당 테이블/서비스도 new에 없음). 편집을 열어두면 사용자 입력이 저장 시 조용히 사라져서 조회 전용으로 둔다.
    { headerName: "FP_ORDER_PEGGING", field: "orderPeggingCd", colId: "orderPeggingCd", type: ["nonEditableColumn"], width: 110 },
    // 부가 정보 그룹(2줄 헤더, 접기/펼치기) — old sideInfoGroup(expanded:false) 대응.
    {
      headerName: "FP_EXTRA_INFO", groupId: "extraInfo", marryChildren: true, openByDefault: false,
      children: [
        { headerName: "FP_USE_YN", field: "activeYn", colId: "activeYn", type: ["booleanColumn", "centerAligned"], width: 110 },
        { headerName: "FP_INFINITE_STOCK_USE_TP", field: "infiniteTpCd", colId: "infiniteTpCd", cellClass: "stringType", width: 150,
          type: ["centerAligned"], cellEditor: "agRichSelectCellEditor", cellEditorPopup: true, columnGroupShow: "open",
          cellEditorParams: () => codeComboCellEditorParams("FP_INFINITE_TP_CD", infiniteTpValuesRef, t) }
      ]
    },
    // 수정 정보(audit) 그룹 — Route.js/Resource.js와 동일 FP_COL_AUDIT 그룹 패턴 재사용.
    {
      headerName: "FP_COL_AUDIT", groupId: "audit", marryChildren: true, openByDefault: false,
      children: [
        { headerName: "updatedTs", field: "modifyDttm", colId: "modifyDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, filterType: "timestamp" },
        { headerName: "updatedBy", field: "modifyUser", colId: "modifyUser", type: ["nonEditableColumn", "centerAligned"], width: 110, filterType: "string", columnGroupShow: "open" },
        { headerName: "createdTs", field: "createDttm", colId: "createDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, filterType: "timestamp", columnGroupShow: "open" },
        { headerName: "createdBy", field: "createUser", colId: "createUser", type: ["nonEditableColumn", "centerAligned"], width: 110, filterType: "string", columnGroupShow: "open" }
      ]
    },
    // old visible:false 4개(§3-1a 컬럼 개수 대조로 발견) — StockDto엔 필드가 있으나 old도 화면에
    // 안 보여주던 값이라 숨김 유지, 저장 왕복만 보존.
    { headerName: "FP_PROJECTION_INVENTORY_CD", field: "projectionInventoryCd", colId: "projectionInventoryCd", hide: true, type: ["nonEditableColumn"] },
    { headerName: "FP_PROJECTION_WO_CD", field: "projectionWoCd", colId: "projectionWoCd", hide: true, type: ["nonEditableColumn"] },
    { headerName: "FP_PROJECTION_WO_DUE_DT", field: "projectionWoDueDate", colId: "projectionWoDueDate", hide: true, type: ["nonEditableColumn"] },
    { headerName: "FP_GRADE", field: "grade", colId: "grade", hide: true, type: ["nonEditableColumn"] }
  ];

  const retrieveStock = () => {
    if (!planScope) return;

    const reqId = ++latestReqRef.current;
    gridRef.current?.api?.setGridOption("loading", true);

    // planScope 단위로 전량 로드 → 품목/인벤토리/재고 코드 필터는 서버가 아니라 AG Grid 외부 필터(클라)에서 처리.
    fpStockService.getAll({ planScope })
      .then(response => {
        if (reqId !== latestReqRef.current) return;
        const results = response.data;
        if (isEmpty(results)) {
          setStock([]);
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        } else {
          setStock(results);
        }
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        if (reqId !== latestReqRef.current) return;
        const reduxState = reduxUtil.createObjectState(viewName, { planScope, ...searchFields });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
        gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onGridReady = params => {
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  };

  const insertRow = () => {
    // old defaultValue(qty "0", priority 1, activeYn true) + 파티션 키(planScope) 반영.
    const newRow = {
      id: null,
      planScope,
      stockCd: "",
      descTxt: "",
      inventoryCd: "",
      inventoryNm: "",
      itemCd: "",
      itemNm: "",
      itemClassCd: "",
      usableTs: null,
      expireTs: null,
      qty: 0,
      // 발주/입고 정보 5필드는 조회 전용(백엔드가 채움) — 신규 행에서는 빈 값.
      orderNumber: "",
      orderTs: null,
      account: "",
      orderManager: "",
      expectedReceiptTs: null,
      receiptTs: null,
      priority: 1,
      orderPeggingCd: "",
      activeYn: true,
      infiniteTpCd: "",
      projectionInventoryCd: "",
      projectionWoCd: "",
      projectionWoDueDate: null,
      grade: ""
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  };

  const removeStock = () => {
    gridRef.current.api.stopEditing();
    const selectedRows = gridRef.current.api.getSelectedRows();

    if (selectedRows.length > 0) {
      fpStockService.remove(selectedRows)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
        })
        .then(() => {
          retrieveStock();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const saveStock = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);

    if (allStateData.length === 0) {
      setProgressOn(false);
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    // 실제로 비어 있는 필드만 안내한다 — 필수 필드 이름을 항상 전부 나열하면
    // 무엇을 채워야 하는지 알 수 없다(입고일 하나가 비어도 재고 코드까지 같이 뜬다).
    const missingRequired = findMissingRequired(gridRef.current.api, REQUIRED_FIELDS);
    if (missingRequired.length > 0) {
      setProgressOn(false);
      const labels = [...new Set(missingRequired.map(m => t(m.label)))].join("/");
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_PleaseEnterValues", { values: labels }) });
      focusInvalidCell(gridRef.current.api, missingRequired[0]);
      return;
    }

    fpStockService.upsert(allStateData)
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        debug(e);
      })
      .then(() => {
        setProgressOn(false);
        retrieveStock();
      });
  };

  // 인벤토리 선택 시 품목 정보까지 함께 write-back — old setInventoryValues의 스프레드 대상 5필드와 동일.
  const onSelectInventory = (row) => {
    const node = inventoryPopup.node;
    if (node) {
      node.setDataValue("inventoryCd", row.inventoryCd);
      node.setDataValue("inventoryNm", row.inventoryNm);
      node.setDataValue("itemCd", row.itemCd);
      node.setDataValue("itemNm", row.itemNm);
      node.setDataValue("itemClassCd", row.itemClassCd);
    }
  };

  const handleDialog = (action) => {
    if (action === "SAVE") {
      setProgressOn(true);
      saveStock();
    } else if (action === "DELETE") {
      removeStock();
    }
  };

  // TODO(MIG-T2): [stock/Stock] location.state 딥링크 자동조회 + 정확일치 stockCd 검색필드 미이관.
  //   old: 타 화면에서 {planScope, stockCd}를 state로 넘겨 진입하면 그 값으로 세팅 후 자동조회.
  //   사유: 화면 간 이동 state 전달은 component-mapping-table.md §8-1 결론대로 Tier2 유보(Resource와 동일).
  //   AUTO_LOAD 플래그는 new 관례(항상 자동조회)로 이미 동등 대체됨(§8-2) — 별도 이관 불필요.
  useEffect(() => {
    if (!gridLoading) return;
    retrieveStock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridLoading, planScope]);

  return (
    <>
      <FilterContainer>
        <PlanScope value={planScope} onChange={setPlanScope} />
        <SearchOptsGroup
          fields={searchFields}
          fieldKeys={SEARCH_FIELD_KEYS}
          onChange={onSearchFieldChange}
          labels={SEARCH_FIELD_LABELS}
        />
        <Box sx={{ mt: 1.5, ml: "auto", mr: 1.5 }}>
          <DownloadButton onClick={() => gridRef.current.api.exportDataAsExcel({ fileName: `${t(title)}.xlsx`, sheetName: t(title) })} />
          <AddButton onClick={insertRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
        </Box>
      </FilterContainer>
      <Box id="fp-stock-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, minHeight: 0, pb: 1 }}>
        <AgGridReact
          ref={gridRef}
          rowData={stock}
          {...defaultGridMemo}
          rowSelection={{
            ...defaultGridMemo.rowSelection,
            headerCheckbox: true,
            checkboxes: true
          }}
          onGridReady={onGridReady}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
        />
      </Box>
      <InventoryPopup
        open={inventoryPopup.open}
        onClose={() => setInventoryPopup({ open: false, node: null })}
        planScope={planScope}
        onSelect={onSelectInventory}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Backdrop sx={{ color: "#fff", zIndex: 10000 }} open={progressOn}>
        <CircularProgress color="inherit" size={"80px"} />
      </Backdrop>
    </>
  );
};

export default withTranslation()(Stock);
